import {
  DeleteOutlined,
  DownloadOutlined,
  EyeOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import {
  Button,
  Card,
  Col,
  DatePicker,
  Descriptions,
  Form,
  Input,
  message,
  Row,
  Spin,
  Typography,
} from 'antd';
import {
  getCatalogoDePadrones,
  getContenTypePorTipoDePadron,
  getTipoDePadronPorContenType,
} from 'api/catalogos';
import { getMetodosDePago } from 'api/configuracion';
import { getContribuyenteCaja } from 'api/empresas/contribuyentes';
import { getConvenios, getParcialidades } from 'api/recaudacion/convenios';
import { getTiposDeConvenios } from 'api/recaudacion/tiposDeConvenio';
import { retrievePadronesMap } from 'api/recaudacion/utils';
import { postObtenerReporte } from 'api/reporteador';
import FormSubmitControls from 'components/FormSubmitControls';
import InputSearch from 'components/InputSearch';
import { downloadBase64 } from 'components/Previewer';
import Select from 'components/Select';
import Table from 'components/Table';
import Moment from 'moment';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { DATE_FRONTEND_FORMAT, discartNullValues } from 'utils/formatValues';
import { onError } from 'utils/handlers';
import { toUpper } from 'utils/normalizers';
import { gettersPadronesCajaMap } from 'utils/padrones';
import padronToString from 'utils/padronesToString';
import toCurrency from 'utils/toCurrency';
import { padronesEnum } from 'views/Tramites/GPM/enums';
import { getPredio } from 'api/catastro/predio';
import ParcialidadesDeConvenios from './ParcialidadesDeConvenios';

const { RangePicker } = DatePicker;
const { Text } = Typography;

export const permissionConfig = {
  title: 'Convenios',
  model: 'convenio',
  catalogs: [
    'parcialidaddelconvenio',
    'tipodepadron',
    'tipodeconvenio',
    'contribuyente',
  ],
};

// Componentes de Formulario
const FormularioBusqueda = ({
  form,
  padrones,
  tiposDeConvenios,
  onFinish,
  setTipoDePadron,
  setParams,
  setCurrentConvenio,
  setData,
}) => {
  const rules = {
    tipo_de_padron: [
      {
        validator: async () => {
          const values = form.getFieldsValue();
          if (values.cuenta_objeto && !values.tipo_de_padron) {
            throw new Error('Especifique el tipo de padrón');
          }
        },
      },
    ],
  };

  return (
    <Form
      layout="vertical"
      form={form}
      scrollToFirstError
      onFinish={onFinish}
    >
      <Row gutter={10}>
        <Col xs={24} sm={24} md={12} lg={8}>
          <Form.Item label="Rango de fechas" name="fecha">
            <RangePicker placeholder={['', '']} />
          </Form.Item>
        </Col>

        <Col xs={24} sm={24} md={12} lg={8}>
          <Form.Item label="Tipo de convenio" name="tipo_de_convenio">
            <Select dataSource={tiposDeConvenios} />
          </Form.Item>
        </Col>

        <Col xs={24} sm={24} md={12} lg={8}>
          <Form.Item label="Contribuyente" name="rfc" tooltip="Búsqueda por RFC">
            <Input allowClear />
          </Form.Item>
        </Col>

        <Col xs={24} sm={24} md={12} lg={8}>
          <Form.Item
            label="Padrón"
            rules={rules.tipo_de_padron}
            name="tipo_de_padron"
          >
            <Select
              dataSource={padrones.filter(({ id }) => id !== 5)}
              onChange={setTipoDePadron}
            />
          </Form.Item>
        </Col>

        <Col xs={24} sm={24} md={12} lg={8}>
          <Form.Item label="Cuenta Objeto" name="cuenta_objeto">
            <Input allowClear />
          </Form.Item>
        </Col>

        <Col xs={24} sm={24} md={12} lg={8}>
          <InputSearch
            name="folio"
            label="Folio Convenio"
            onPressEnter={onFinish}
            normalizer={toUpper}
          />
        </Col>

      </Row>
      <Form.Item hidden>
        <Button htmlType="submit" />
      </Form.Item>
      <Row gutter={[10, 10]}>
        <Col xs={24} sm={12} md={12} lg={8}>
          <Button htmlType="submit" block icon={<SearchOutlined />}>
            Buscar
          </Button>
        </Col>
        <Col xs={24} sm={12} md={12} lg={8}>
          <Button
            onClick={() => {
              form.resetFields();
              setParams({});
              setCurrentConvenio(null);
              setData({ results: [] });
            }}
            block
            icon={<DeleteOutlined />}
          >
            Limpiar búsqueda
          </Button>
        </Col>
      </Row>
    </Form>
  );
};

FormularioBusqueda.propTypes = {
  form: PropTypes.shape({
    getFieldsValue: PropTypes.func.isRequired,
    resetFields: PropTypes.func.isRequired,
    validateFields: PropTypes.func.isRequired,
  }).isRequired,
  padrones: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number,
    descripcion: PropTypes.string,
  })).isRequired,
  tiposDeConvenios: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number,
    descripcion: PropTypes.string,
  })).isRequired,
  onFinish: PropTypes.func.isRequired,
  setTipoDePadron: PropTypes.func.isRequired,
  setParams: PropTypes.func.isRequired,
  setCurrentConvenio: PropTypes.func.isRequired,
  setData: PropTypes.func.isRequired,
};

// Componente de Detalle
const DetalleConvenio = ({
  currentConvenio,
  padrones,
  onCancel,
  permission,
}) => {
  const [formInfo] = Form.useForm();
  const [descriptionItems, setDescriptionItems] = useState([]);

  useEffect(() => {
    if (currentConvenio) {
      const tipoPadron = getTipoDePadronPorContenType(currentConvenio.content_type_padron);
      const padron = padrones.find(({ id }) => id === tipoPadron);
      const importeTotal = currentConvenio.total_convenido_de_adeudos
      - currentConvenio.total_convenido_de_descuentos;
      const importeAdeudado = importeTotal - currentConvenio.pagado;

      const items = [
        { label: 'Resolución', value: currentConvenio?.oficio_de_resolucion || '' },
        { label: 'Folio', value: currentConvenio?.folio || '' },
        { label: 'Fecha de alta', value: currentConvenio.fecha_de_creacion?.format(DATE_FRONTEND_FORMAT) || '' },
        { label: 'Fecha de resolución', value: currentConvenio.fecha_de_resolucion?.format(DATE_FRONTEND_FORMAT) || '' },
        { label: 'Fecha de último pago', value: currentConvenio.fecha_ultimo_pago?.format(DATE_FRONTEND_FORMAT) || '' },
        { label: 'Importe total', value: toCurrency(importeTotal) },
        { label: 'Objeto Cuenta', value: currentConvenio?.padron_convenido?.cuenta_objeto || currentConvenio?.padron_convenido?.clave || currentConvenio?.padron_convenido?.folio },
        { label: 'Número de pagos', value: currentConvenio.numero_de_pagos || '' },
        { label: 'Importe pagado', value: toCurrency(currentConvenio.pagado) },
        { label: 'Padrón', value: padron?.descripcion || '' },
        { label: 'Pagos vencidos', value: currentConvenio.pagos_vencidos ?? '' },
        { label: 'Importe adeudado', value: toCurrency(importeAdeudado) },
        { label: 'Observaciones', value: currentConvenio.observaciones || '' },
      ];
      setDescriptionItems(items);
    }
  }, [currentConvenio, padrones]);

  return (
    <Card
      className="form-container"
      title="Información Recibo"
      extra={<FormSubmitControls onCancel={onCancel} />}
      bordered={false}
    >
      <Form layout="vertical" form={formInfo} className="no-margin">
        <Row gutter={10}>
          <Col xs={24} sm={24} md={24} lg={24}>
            <Descriptions
              bordered
              size="small"
              layout="horizontal"
              column={{
                xs: 1,
                sm: 2,
                lg: 3,
              }}
            >
              {descriptionItems.map(({ label, value }) => (
                <Descriptions.Item key={label} label={<Text>{label}</Text>}>
                  {value}
                </Descriptions.Item>
              ))}
            </Descriptions>
          </Col>
          <Col xs={24} sm={24} md={24} lg={24}>
            <ParcialidadesDeConvenios
              data={currentConvenio.i_parcialidades}
              permission={permission}
            />
          </Col>
        </Row>
      </Form>
    </Card>
  );
};

DetalleConvenio.propTypes = {
  currentConvenio: PropTypes.shape({
    id: PropTypes.number,
    content_type_padron: PropTypes.number,
    object_id_padron: PropTypes.number,
    pagos_vencidos: PropTypes.number,
    observaciones: PropTypes.string,
    oficio_de_resolucion: PropTypes.string,
    folio: PropTypes.string,
    fecha_de_creacion: PropTypes.string,
    fecha_de_resolucion: PropTypes.string,
    fecha_ultimo_pago: PropTypes.string,
    total_convenido_de_adeudos: PropTypes.number,
    total_convenido_de_descuentos: PropTypes.number,
    pagado: PropTypes.number,
    numero_de_pagos: PropTypes.number,
    i_parcialidades: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.number,
      adeudo: PropTypes.number,
      importe_original: PropTypes.number,
      importe_de_la_parcialidad: PropTypes.number,
      total_abonado: PropTypes.number,
      sobrante: PropTypes.number,
      saldado: PropTypes.bool,
      fecha_de_pago: PropTypes.string,
      fecha_de_vencimiento: PropTypes.string,
      mensualidad: PropTypes.number,
    })),
    padron_convenido: PropTypes.shape({
      cuenta_objeto: PropTypes.string,
      clave: PropTypes.string,
      folio: PropTypes.string,
    }),
  }).isRequired,
  padrones: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number,
    descripcion: PropTypes.string,
  })).isRequired,
  onCancel: PropTypes.func.isRequired,
  permission: PropTypes.shape({
    view_convenio: PropTypes.bool,
    view_datodepago: PropTypes.bool,
  }).isRequired,
};

const ConsultaDeConvenios = ({ permission }) => {
  const timeoutConvenios = useRef();
  const { padrones } = useSelector(({ catalogs }) => catalogs);
  const [form] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({ results: [] });
  const [visible, setVisible] = useState(false);
  const [params, setParams] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [tiposDeConvenios, setTiposDeConvenios] = useState([]);
  const [currentConvenio, setCurrentConvenio] = useState(null);
  const [tipoDePadron, setTipoDePadron] = useState(null);

  const getFullFileName = (padron) => `${Moment().format('YYYYMMDD HH:mm:ss')} - SIR - Convenio - ${padronToString(padron, tipoDePadron)}.pdf`;

  useEffect(() => {
    let mounted = true;
    const fetch = async () => {
      setLoading(true);
      try {
        const [_tiposDeConvenios] = await Promise.all([
          getTiposDeConvenios(),
          getMetodosDePago(),
          getCatalogoDePadrones(),
        ]);
        if (mounted) {
          setTiposDeConvenios(_tiposDeConvenios);
        }
      } catch (error) {
        onError(error);
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };
    fetch();
    return () => { mounted = false; };
  }, []);

  const fetchPredio = async (q) => {
    const prePredio = await gettersPadronesCajaMap[padronesEnum.PREDIO](q);
    if (prePredio?.results?.length === 0) {
      return [];
    }
    if (prePredio?.results?.length > 1) {
      return prePredio?.results;
    }
    const predio = await getPredio(prePredio?.[0]?.id, false);
    return [predio];
  };

  const fetchPadronById = async (id, tipoPadron) => {
    try {
      const getters = {
        ...gettersPadronesCajaMap,
        [padronesEnum.PREDIO]: fetchPredio,
      };
      const results = await getters[tipoPadron]({ id });

      const padron = Array.isArray(results) ? results : results.results;
      if (padron?.length === 0) {
        message.warn('No se encontró el padrón');
        return null;
      }
      return padron[0];
    } catch (error) {
      onError(error);
      return null;
    }
  };

  const fetchPadron = async (q, tipoPadron) => {
    try {
      const padron = await gettersPadronesCajaMap[tipoPadron]({ q });
      if (padron?.results?.length === 0 || padron?.length > 1) {
        message.warn('No se encontró el padrón');
        return null;
      }
      return padron?.[0] || padron?.results?.[0];
    } catch (error) {
      onError(error);
      return null;
    }
  };

  const fetchConvenios = async (page = 1, _params = null) => {
    try {
      setLoading(true);
      setCurrentPage(page);
      const search = { ..._params, ...params };
      const _convenios = await getConvenios({ page, ...search });

      const promises = _convenios.results.map((c) => {
        const tipoPadron = getTipoDePadronPorContenType(c.content_type_padron);
        return fetchPadronById(c.object_id_padron, tipoPadron);
      });

      const promisesParcialidades = _convenios.results
        .map((c) => getParcialidades({ convenio: c.id }));

      const _padronesConvenidos = await Promise.all(promises);
      const _parcialidades = await Promise.all(promisesParcialidades);

      const results = _convenios.results.map((c) => {
        const parcialidades = _parcialidades.flat().filter((p) => p?.convenio === c.id);
        const padronConvenio = _padronesConvenidos.find((p) => p?.id === c.object_id_padron);
        const contribuyenteObj = padronConvenio?.contribuyente || padronConvenio?.contributente;

        const contribuyente = contribuyenteObj
          ? padronToString(contribuyenteObj, 15)
          : '';

        return {
          ...c,
          padron_convenido: padronConvenio,
          i_parcialidades: parcialidades,
          fecha_fin: parcialidades
            .sort((a, b) => a.mensualidad - b.mensualidad)
            .at(-1)?.fecha_de_vencimiento,
          contribuyente,
        };
      });

      setData({ ..._convenios, results });
    } catch (err) {
      setData({ results: [] });
      onError(err);
    } finally {
      setLoading(false);
    }
  };

  const onCancel = () => {
    setVisible(false);
    form.resetFields();
    setParams({});
    setCurrentConvenio(null);
    setTipoDePadron(null);
  };

  const onClickExpand = async () => {
    const _currentConvenio = data.results.find(({ id }) => id === selectedRowKeys[0]);
    setCurrentConvenio(_currentConvenio);
    setVisible(true);
  };

  const fetchContribuyente = async (q) => {
    try {
      const contribuyentes = await getContribuyenteCaja({ q });
      if (contribuyentes.results.length === 0) {
        message.warn('No se encontró el contribuyente');
        return null;
      }
      if (contribuyentes.results.length > 1) {
        message.warn('Se encontraron varios contribuyentes, se tomará el primero');
      }
      return contribuyentes.results[0];
    } catch (error) {
      onError(error);
      return null;
    }
  };

  const onFinish = async () => {
    try {
      clearTimeout(timeoutConvenios.current);
      setSelectedRowKeys([]);
      await form.validateFields();
      const search = discartNullValues(form.getFieldsValue());

      if (!Object.keys(search).length) {
        message.warn('Ingrese al menos un criterio de búsqueda');
        return;
      }

      if (search?.cuenta_objeto) {
        await form.validateFields(['tipo_de_padron']);
        const padron = await fetchPadron(search.cuenta_objeto, search.tipo_de_padron);
        if (!padron) return;
        search.object_id_padron = padron.id;
        delete search.cuenta_objeto;
      }

      if (search?.fecha) {
        const [startDate, endDate] = search.fecha.map((date) => date.format('YYYY-MM-DD'));
        search.fecha_de_creacion_after = startDate;
        search.fecha_de_creacion_before = endDate;
        delete search.fecha;
      }

      if (search?.tipo_de_padron) {
        search.content_type_padron = getContenTypePorTipoDePadron(search.tipo_de_padron);
        delete search.tipo_de_padron;
      }

      if (search?.contribuyente) {
        const contribuyente = await fetchContribuyente(search.contribuyente);
        if (!contribuyente) return;
        search.contribuyente_id = contribuyente.id;
        delete search.contribuyente;
      }

      setParams(search);
      await fetchConvenios(1, search);
    } catch (err) {
      onError(err);
    }
  };

  const getBase64Comprobante = async () => {
    try {
      setLoading(true);
      const {
        id,
        tipo_de_convenio,
        object_id_padron,
      } = data.results.find((c) => c.id === selectedRowKeys[0]);

      const padron = await retrievePadronesMap[tipoDePadron](object_id_padron);

      const values = {
        ID: tipo_de_convenio.reportes_de_convenio_id,
        Parametros: [
          {
            Nombre: 'convenio',
            TipoParametro: 'Entero',
            Valor: id,
          },
        ],
      };
      const _base64 = await postObtenerReporte(values);
      if (_base64) {
        downloadBase64(_base64, getFullFileName(padron));
      } else {
        message.warn('No se pudo obtener el comprobante');
      }
    } catch (error) {
      onError(error);
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      titleText: 'Folio',
      dataIndex: 'folio',
      key: 'folio',
      width: 240,
    },
    {
      titleText: 'Fecha de creación',
      dataIndex: 'fecha_de_creacion',
      key: 'fecha_de_creacion',
      width: 150,
      render: (value) => value?.format(DATE_FRONTEND_FORMAT),
    },
    {
      titleText: 'Fin del convenio',
      dataIndex: 'fecha_fin',
      key: 'fecha_fin',
      width: 150,
      render: (value) => value?.format(DATE_FRONTEND_FORMAT),
    },
    {
      titleText: 'Número de parcialidades',
      dataIndex: 'parcialidades',
      key: 'parcialidades',
      width: 200,
      align: 'center',
    },
    {
      titleText: 'Importe total',
      dataIndex: 'total',
      key: 'total',
      width: 150,
      align: 'right',
      render: (value) => toCurrency(value),
    },
    {
      titleText: 'Padrón',
      dataIndex: 'padron_conveniado',
      key: 'padron_conveniado',
      width: 200,
      render: (id) => padrones.find(({ id: padronId }) => padronId === id)?.descripcion,
    },
    {
      titleText: 'Cuenta Objeto',
      dataIndex: 'cuenta_objeto',
      key: 'cuenta_objeto',
      width: 150,
      render: (_, { padron_convenido }) => padron_convenido?.cuenta_objeto
        ?? padron_convenido?.clave
        ?? padron_convenido?.folio
        ?? '-',
    },
    {
      titleText: 'Contribuyente',
      dataIndex: 'contribuyente',
      key: 'contribuyente',
      width: 400,
    },
  ];

  return (
    <Row align="center" justify="center" className="container">
      <Spin tip="Cargando..." spinning={loading}>
        {!visible ? (
          <>
            <FormularioBusqueda
              form={form}
              padrones={padrones}
              tiposDeConvenios={tiposDeConvenios}
              onFinish={onFinish}
              setTipoDePadron={setTipoDePadron}
              setParams={setParams}
              setCurrentConvenio={setCurrentConvenio}
              setData={setData}
            />
            <br />
            <Table
              data={data.results}
              rowSelection={{
                selectedRowKeys,
                type: 'radio',
              }}
              handleOnRowClick={(record) => setSelectedRowKeys([record?.id])}
              cols={columns}
              rowKey="id"
              allowSearch={false}
              allowSort={false}
              customActions={[
                {
                  onClick: onClickExpand,
                  icon: <EyeOutlined />,
                  text: 'Ver detalle',
                  permissions: {
                    model: 'convenio',
                    action: 'view',
                  },
                },
                {
                  icon: <DownloadOutlined />,
                  text: 'Exportar',
                  onClick: getBase64Comprobante,
                  permissions: {
                    model: 'convenio',
                    action: 'view',
                  },
                },
              ]}
              currentPage={currentPage}
              count={data.count}
              pagination={false}
              paginationCallback={fetchConvenios}
            />
          </>
        ) : (
          <DetalleConvenio
            currentConvenio={currentConvenio}
            padrones={padrones}
            onCancel={onCancel}
            permission={permission}
            key={currentConvenio?.id}
          />
        )}
      </Spin>
    </Row>
  );
};

ConsultaDeConvenios.propTypes = {
  permission: PropTypes.shape({
    autorizar_recibo: PropTypes.bool,
    cancelar_recibo: PropTypes.bool,
    add_observacionesrecibo: PropTypes.bool,
    desligar_factura: PropTypes.bool,
    cancelar_pago: PropTypes.bool,
  }).isRequired,
};

export default ConsultaDeConvenios;
